import type { WatchlistAccount } from '@interfaces/watchList';
import { Collapse } from '@mui/material';
import {
  DataTable,
  DEFAULT_TABLE_ROWS_PER_PAGE,
} from '@ovComponents/2-component/dataTable';
import { Box, Typography } from 'hephaestus/web';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useWatchList, WATCH_LIST_APPROACH_THRESHOLD } from './useWatchList';
import { WatchListEmptyState } from './watchListEmptyState';
import { WatchListFooter } from './watchListFooter';
import { WatchListHeader } from './watchListHeader';

/** Scroll cap for the table body — header and footer stay fixed. */
const TABLE_MAX_HEIGHT = '24vh';

export type WatchListProps = {
  /**
   * When provided, a "Configure" link appears in the footer.
   * When undefined the link is hidden — avoids shipping a dead control.
   * Mirrors the `onViewAllClick` pattern from the Drift Queue header.
   */
  onConfigureClick?: () => void;
};

/**
 * Watch List section component.
 *
 * VIEW-ONLY: per AC #6, do not wire row click navigation here.
 * `useNavigate` is intentionally absent from this component.
 */
export const WatchList: FC<WatchListProps> = ({ onConfigureClick }) => {
  const { table, approachingCount, totalRowCount, isLoading, error } =
    useWatchList();

  const [expanded, setExpanded] = useState(() => approachingCount > 0);

  // Auto-open the panel when data transitions from 0 → >0 approaching accounts.
  // Never auto-collapse a panel the user explicitly expanded.
  useEffect(() => {
    if (approachingCount > 0) {
      setExpanded(true);
    }
  }, [approachingCount]);

  const { pageSize } = table.getState().pagination;

  return (
    <Box>
      <WatchListHeader
        approachingCount={approachingCount}
        expanded={expanded}
        onToggleExpanded={() => setExpanded((v) => !v)}
        approachThreshold={WATCH_LIST_APPROACH_THRESHOLD}
      />

      {error !== null && (
        <Typography
          variant='bodySmall'
          component='div'
          sx={{ color: 'error.main', py: 1 }}
        >
          {error.message}
        </Typography>
      )}

      <Collapse in={expanded} collapsedSize={0} unmountOnExit>
        {approachingCount === 0 ? (
          <WatchListEmptyState />
        ) : (
          <>
            <Box
              sx={{
                maxHeight: TABLE_MAX_HEIGHT,
                overflowY: 'auto',
                '& .MuiTableHead-root .MuiTableCell-root': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: 'background.paper',
                },
              }}
            >
              <DataTable<WatchlistAccount>
                table={table}
                rowsPerPage={pageSize}
                loading={isLoading}
                tablePagination={
                  totalRowCount > DEFAULT_TABLE_ROWS_PER_PAGE
                    ? {
                        mode: 'tanstack',
                        showPerPageSelect: true,
                        pageSizes: [DEFAULT_TABLE_ROWS_PER_PAGE, 25, 50],
                      }
                    : {
                        mode: 'tanstack',
                        showPerPageSelect: false,
                        pageSizes: [DEFAULT_TABLE_ROWS_PER_PAGE],
                      }
                }
                // VIEW-ONLY: per AC #6, do not wire row click navigation.
                getRowProps={() => ({ hover: false })}
              />
            </Box>
            <WatchListFooter
              shown={totalRowCount}
              approachThreshold={WATCH_LIST_APPROACH_THRESHOLD}
              onConfigureClick={onConfigureClick}
            />
          </>
        )}
      </Collapse>
    </Box>
  );
};
